import type { DraftTransaction, ImportDraft } from '@finapp/types';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DraftTransactionList } from '../components/DraftTransactionList';
import { apiUrl } from '../config/api';

export const ImportDraftPage: React.FC = () => {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();

  const [draft, setDraft] = useState<ImportDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchDraft = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(apiUrl(`import-drafts/${draftId}`), {
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error('Failed to fetch draft');
      }

      const draftData = await response.json();
      setDraft(draftData);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load draft');
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  // Fetch draft data
  useEffect(() => {
    if (!draftId) return;

    fetchDraft();
  }, [draftId, fetchDraft]);

  // Helper function to calculate validation summary
  const calculateValidationSummary = (transactions: DraftTransaction[]) => {
    const total_transactions = transactions.length;
    const complete_transactions = transactions.filter((t) => t.is_complete).length;
    const incomplete_transactions = total_transactions - complete_transactions;
    const total_errors = transactions.reduce((sum, t) => sum + t.validation_errors.length, 0);

    return {
      total_transactions,
      complete_transactions,
      incomplete_transactions,
      total_errors,
    };
  };

  const handleUpdateTransaction = async (
    transactionId: string,
    updates: Partial<DraftTransaction>
  ) => {
    if (!draft) return;

    try {
      setSaving(true);

      // Optimistically update the UI
      const updatedTransactions = draft.transactions.map((transaction) =>
        transaction.id === transactionId ? { ...transaction, ...updates } : transaction
      );

      // Recalculate validation summary
      const validation_summary = calculateValidationSummary(updatedTransactions);

      setDraft({
        ...draft,
        transactions: updatedTransactions,
        validation_summary,
        updated_at: new Date().toISOString(),
      });

      // Send update to server - handle different types of updates
      let response: Response;

      if (updates.costpoints !== undefined) {
        // Costpoint updates go to the costpoints endpoint
        response = await fetch(
          apiUrl(`import-drafts/${draftId}/transactions/${transactionId}/costpoints`),
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              costpoints: updates.costpoints,
            }),
          }
        );
      } else {
        // Other updates (heading, description, VAT, etc.) go to the general transaction endpoint.
        // vat_rate is frontend-only (for optimistic display); the backend derives it from vat_id.
        const { vat_rate: _, ...apiUpdates } = updates;
        response = await fetch(apiUrl(`import-drafts/${draftId}/transactions/${transactionId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(apiUpdates),
        });
      }

      if (!response.ok) {
        throw new Error('Failed to update transaction');
      }

      toast.success('Transaction updated', { autoClose: 1500 });

      // Refresh draft for non-costpoint updates so joined fields (e.g. vat_rate) reflect the
      // saved state. Costpoints use the optimistic update only to avoid collapsing the open row.
      if (updates.costpoints === undefined) {
        await fetchDraft();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update transaction');
      await fetchDraft();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!draft) return;

    try {
      setSaving(true);
      // Just save the current draft state to Redis (lightweight operation)
      // Note: This should ideally call a save endpoint, but for now we'll skip
      // the expensive validation and only save when needed
      console.log('Draft saved locally (validation skipped for performance)');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleValidateDraft = async () => {
    if (!draft) return;

    try {
      setSaving(true);

      const response = await fetch(apiUrl(`import-drafts/${draftId}/validate`), {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error('Failed to validate draft');
      }

      // Update the draft state with the validated response instead of refetching
      const validatedDraft = await response.json();
      setDraft(validatedDraft);
      toast.success('Draft validated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to validate draft');
    } finally {
      setSaving(false);
    }
  };

  const handlePartialCommitDraft = async () => {
    if (!draft) return;

    const completedCount = draft.transactions.filter((t) => t.is_complete).length;
    const remainingCount = draft.transactions.length - completedCount;

    if (completedCount === 0) {
      toast.error(
        'No completed transactions to commit. Please complete at least one transaction first.'
      );
      return;
    }

    const confirmCommit = window.confirm(
      `Are you sure you want to commit ${completedCount} completed transaction(s) to the database? ${remainingCount} transaction(s) will remain in the draft for further editing.`
    );

    if (!confirmCommit) return;

    try {
      setSaving(true);

      const response = await fetch(apiUrl(`import-drafts/${draftId}/partial-commit`), {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error('Failed to partially commit draft');
      }

      const result = await response.json();

      if (result.remaining_count === 0) {
        // All transactions were committed, navigate to transactions page
        navigate('/transactions', {
          state: {
            message: `Successfully committed ${result.committed_count} transactions!`,
          },
        });
      } else {
        // Some transactions remain, refresh the draft
        await fetchDraft();
        toast.success(
          `Committed ${result.committed_count} transactions. ${result.remaining_count} remaining in draft.`
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to partially commit draft');
    } finally {
      setSaving(false);
    }
  };

  const handleCommitDraft = async () => {
    if (!draft) return;

    const confirmCommit = window.confirm(
      'Are you sure you want to commit this draft to the database? This action cannot be undone.'
    );

    if (!confirmCommit) return;

    try {
      setSaving(true);

      const response = await fetch(apiUrl(`import-drafts/${draftId}/commit`), {
        method: 'POST',
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error('Failed to commit draft');
      }

      // Navigate to transactions page after successful commit
      navigate('/transactions', {
        state: { message: 'Draft committed successfully!' },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to commit draft');
    } finally {
      setSaving(false);
    }
  };

  const handleAbandonDraft = async () => {
    if (!draft) return;

    const confirmAbandon = window.confirm(
      'Are you sure you want to abandon this draft? All progress will be lost.'
    );

    if (!confirmAbandon) return;

    try {
      setSaving(true);

      const response = await fetch(apiUrl(`import-drafts/${draftId}/abandon`), {
        method: 'DELETE',
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error('Failed to abandon draft');
      }

      // Navigate back to transactions page
      navigate('/transactions', {
        state: { message: 'Draft abandoned.' },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to abandon draft');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading draft...</p>
        </div>
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Draft Not Found</h2>
          <p className="text-gray-600 mb-4">The requested draft could not be found.</p>
          <button
            onClick={() => navigate('/transactions')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <DraftTransactionList
          draft={draft}
          onUpdateTransaction={handleUpdateTransaction}
          onSaveDraft={handleSaveDraft}
          onValidateDraft={handleValidateDraft}
          onPartialCommitDraft={handlePartialCommitDraft}
          onCommitDraft={handleCommitDraft}
          onAbandonDraft={handleAbandonDraft}
          onRefreshDraft={fetchDraft}
          isLoading={saving}
        />
      </div>
    </div>
  );
};
